import { useState } from "react";
import { Brain, ChevronDown, ChevronUp, BookOpen, Gauge, AlertTriangle } from "lucide-react";

/**
 * "Show Reasoning" expandable panel for ARIA responses.
 *
 * Renders three sections (per ARIA persona spec):
 *   • Sources consulted
 *   • Confidence level (High / Medium / Low)
 *   • Known limitations
 *
 * Props:
 *   reasoning: {
 *     sources:    string[]   — what informed this response
 *     confidence: "High" | "Medium" | "Low"
 *     limitations:string[]   — what ARIA could not see / known caveats
 *   }
 *   compact: boolean — tighter layout for the voice card
 *   defaultOpen: boolean — start expanded (rare; used in case-detail surfaces)
 */
const CONFIDENCE_TONE = {
  High: { text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40" },
  Medium: { text: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/40" },
  Low: { text: "text-red-300", bg: "bg-red-500/10", border: "border-red-500/40" },
};

export default function ReasoningPanel({
  reasoning,
  compact = false,
  defaultOpen = false,
  surface = "generic",
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (!reasoning) return null;
  const conf = reasoning.confidence || "Medium";
  const tone = CONFIDENCE_TONE[conf] || CONFIDENCE_TONE.Medium;
  const sources = reasoning.sources || [];
  const limitations = reasoning.limitations || [];

  const wrapCls = compact
    ? "mt-3 pt-3 border-t border-[var(--pa-border)]"
    : "mt-3 rounded-md border border-[var(--pa-border)] bg-[var(--pa-bg-2)] p-3";

  return (
    <div data-testid={`reasoning-panel-${surface}`} className={wrapCls}>
      <button
        type="button"
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        data-testid={`reasoning-toggle-${surface}`}
        className="w-full flex items-center justify-between gap-2 text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition"
      >
        <span className="flex items-center gap-1.5">
          <Brain size={12} className="text-[var(--pa-gold)]" />
          Show reasoning
          <span
            className={`ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${tone.text} ${tone.bg} ${tone.border}`}
            data-testid={`reasoning-confidence-${surface}`}
          >
            <Gauge size={9} /> {conf}
          </span>
        </span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`reasoning-body-${surface}`}>
          {/* Sources */}
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--pa-gold)]">
              <BookOpen size={10} /> Sources consulted
            </div>
            {sources.length ? (
              <ul className="mt-1.5 space-y-0.5 text-[11px] text-[var(--pa-text)] leading-snug" data-testid={`reasoning-sources-${surface}`}>
                {sources.map((s, i) => (
                  <li key={i} className="border-l border-[var(--pa-gold)]/40 pl-2">{s}</li>
                ))}
              </ul>
            ) : (
              <div className="mt-1 text-[11px] text-[var(--pa-text-dim)] italic">No external sources consulted for this response.</div>
            )}
          </div>

          {/* Limitations */}
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-300">
              <AlertTriangle size={10} /> Known limitations
            </div>
            {limitations.length ? (
              <ul className="mt-1.5 space-y-0.5 text-[11px] text-[var(--pa-text-muted)] leading-snug" data-testid={`reasoning-limitations-${surface}`}>
                {limitations.map((l, i) => (
                  <li key={i} className="border-l border-amber-500/40 pl-2">{l}</li>
                ))}
              </ul>
            ) : (
              <div className="mt-1 text-[11px] text-[var(--pa-text-dim)] italic">No specific limitations flagged.</div>
            )}
          </div>

          <div className="text-[10px] text-[var(--pa-text-dim)] italic pt-1 border-t border-[var(--pa-border)]">
            Reasoning shown for transparency only — this output requires independent clinical verification before use.
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Helpers — build reasoning payloads for each surface
// ──────────────────────────────────────────────────────────────

/** Static reasoning for ARIA's voice-card greeting / command navigation. */
export function reasoningForVoiceCard(spokenText, transcript = null) {
  const sources = ["ARIA persona prompt (Patent Pending US 64/062,147)", "ProsthoAI command vocabulary (24 navigation commands)"];
  if (transcript) sources.push(`Physician voice input: "${transcript}"`);
  return {
    sources,
    confidence: "High",
    limitations: [
      "Voice recognition accuracy depends on browser and microphone quality.",
      "ARIA navigates only to known platform routes; novel queries are not interpreted clinically.",
    ],
  };
}

/** Reasoning for the Daily Huddle briefing. */
export function reasoningForHuddle(huddle) {
  const counts = huddle?.counts || {};
  const window = huddle?.window_days || 30;
  const sources = [
    "Live MongoDB aggregation: quick_assessments, case_analyses, consultations, topology_analyses, reviews, users",
    `Audit log window: ${window} days`,
    `Snapshot generated at ${huddle?.generated_at || "unknown"}`,
  ];
  const limitations = [
    "Counts reflect the moment of fetch — refresh to see live changes.",
    "Red flag detection is based on metadata only; severity must be independently verified in the source record.",
  ];
  if (!counts.red_flags_unacknowledged && !counts.topology_running) {
    limitations.push("Quiet board paths assume no offline notifications — ARIA does not poll external systems.");
  }
  return { sources, confidence: "High", limitations };
}

/** Reasoning for a completed topology analysis. */
export function reasoningForTopology(full) {
  if (!full) return null;
  const cfg = full.topology_config || {};
  const agents = Array.isArray(cfg.modules_activated) ? cfg.modules_activated : Array.isArray(full.modules_activated) ? full.modules_activated : [];
  const complexity = full.complexity || cfg.complexity || "unknown";
  const sources = [
    `Topology classifier output (complexity: ${complexity})`,
    agents.length ? `Specialist agents run in parallel: ${agents.join(", ")}` : "No specialist agents were activated for this case.",
    "Synthesis agent (board-certified prosthodontist persona) integrated all agent outputs",
    "Knowledge base: peer-reviewed prosthodontic, periodontal, and implant literature",
  ];
  const limitations = [
    "AI synthesis depends on the quality and completeness of the case input you provided.",
    "Radiographic interpretation is descriptive only — independent specialist review remains essential.",
    "Treatment options are presented as planning support, not a definitive prescription.",
  ];
  // Confidence heuristic: more agents + complex case → Medium (more moving parts);
  // straightforward case with synthesis → High.
  const confidence = complexity === "complex" ? "Medium" : "High";
  return { sources, confidence, limitations };
}
